import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Undo2 } from "lucide-react";
import { toast } from "sonner";

export default function SalesReturn() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [returnItems, setReturnItems] = useState({});
  const [reason, setReason] = useState("");

  const { data: bills } = useQuery({ queryKey: ["sr-bills", search], queryFn: async () => (await api.get("/sales", { params: { search, period: "this_month" } })).data });
  const { data: returns } = useQuery({ queryKey: ["sales-returns"], queryFn: async () => (await api.get("/sales-returns", { params: { period: "this_month" } })).data });

  function selectBill(b) {
    setSelected(b);
    const init = {};
    b.items.forEach((it, i) => { init[i] = { checked: false, qty: it.qty }; });
    setReturnItems(init);
  }

  const refundAmount = selected ? selected.items.reduce((sum, it, i) => {
    const r = returnItems[i];
    if (r?.checked) return sum + (it.line_total / it.qty) * Number(r.qty || 0);
    return sum;
  }, 0) : 0;

  async function submitReturn() {
    const items = selected.items.filter((_, i) => returnItems[i]?.checked).map((it, idx) => {
      const origIdx = selected.items.indexOf(it);
      return { product_id: it.product_id, name: it.name, qty: Number(returnItems[origIdx].qty), price: it.price, tax_rate: it.tax_rate };
    });
    if (!items.length) { toast.error("Select at least one item to return"); return; }
    try {
      await api.post("/sales-returns", { sale_id: selected.id, items, refund_amount: refundAmount, reason });
      toast.success("Sales return processed · Stock updated");
      setSelected(null); setReason("");
      qc.invalidateQueries({ queryKey: ["sales-returns"] });
    } catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-5">
      <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Sales Return / Credit Note</h2>
        <p className="text-slate-500 text-sm">Return items from an existing bill and issue a credit note</p></div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-4 border-slate-200">
          <h3 className="font-display font-semibold text-[#1A2A5E] mb-3">1. Select Original Bill</h3>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search bill no / customer..." className="pl-9" data-testid="sr-search" />
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(bills || []).map((b) => (
              <button key={b.id} onClick={() => selectBill(b)} data-testid={`sr-bill-${b.bill_no}`}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.id === b.id ? "border-[#F4571E] bg-[#FFF3EF]" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex justify-between">
                  <span className="font-mono font-medium text-[#1A2A5E]">{b.bill_no}</span>
                  <span className="font-mono font-semibold">{money(b.grand_total)}</span>
                </div>
                <div className="text-xs text-slate-400">{b.customer_name} · {fmtDateTime(b.created_at)}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4 border-slate-200">
          <h3 className="font-display font-semibold text-[#1A2A5E] mb-3">2. Select Items to Return</h3>
          {selected ? (
            <div className="space-y-3">
              <div className="space-y-2">
                {selected.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                    <Checkbox checked={returnItems[i]?.checked} onCheckedChange={(v) => setReturnItems({ ...returnItems, [i]: { ...returnItems[i], checked: v } })} data-testid={`sr-item-${i}`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{it.name}</div>
                      <div className="text-xs text-slate-400">Sold: {it.qty} · {money(it.price)}</div>
                    </div>
                    <Input type="number" min={1} max={it.qty} value={returnItems[i]?.qty || it.qty} disabled={!returnItems[i]?.checked}
                      onChange={(e) => setReturnItems({ ...returnItems, [i]: { ...returnItems[i], qty: e.target.value } })} className="h-8 w-16 text-center font-mono" />
                  </div>
                ))}
              </div>
              <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Damaged / wrong item..." className="mt-1" /></div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-display font-semibold">Refund Amount</span>
                <span className="font-mono font-bold text-xl text-[#F4571E]">{money(refundAmount)}</span>
              </div>
              <Button className="w-full bg-[#1A2A5E] hover:bg-[#223678]" onClick={submitReturn} data-testid="submit-return">Process Return & Credit Note</Button>
            </div>
          ) : <div className="text-center py-16 text-slate-400"><Undo2 className="h-8 w-8 mx-auto mb-2 opacity-40" />Select a bill to begin</div>}
        </Card>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100"><h3 className="font-display font-semibold text-[#0F172A]">Recent Returns</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Credit Note</TableHead><TableHead>Original Bill</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Refund</TableHead></TableRow></TableHeader>
            <TableBody>
              {(returns || []).length ? returns.map((r) => (
                <TableRow key={r.id}><TableCell className="font-mono text-[#1A2A5E]">{r.credit_note_no}</TableCell>
                  <TableCell className="font-mono">{r.bill_no}</TableCell><TableCell>{r.customer_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-[#F4571E]">{money(r.refund_amount)}</TableCell></TableRow>
              )) : <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No returns yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
